import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import {
  fetchRawRegulations,
  fetchRawRegulationsById,
  updateRawRegulations,
} from './RawRegulations.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';

export const useService = ({
  regulationId,
  pageNumber = 1,
  pageSize = 10,
  searchTerm = '',
}: {
  regulationId: number | null;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
}) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;

  const updateMutation = useMutation({
    mutationFn: updateRawRegulations,
    onSuccess: () => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.regulation') }));
      queryClient.invalidateQueries({ queryKey: ['raw-regulations'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: regulation } = useQuery({
    queryKey: ['raw-regulations', regulationId],
    queryFn: () => fetchRawRegulationsById(regulationId),
    enabled: !!regulationId && hasAccount,
    retry: false,
  });

  const { data: regulations, isLoading, isFetching } = useQuery({
    queryKey: ['raw-regulations', { pageNumber, pageSize, searchTerm }],
    queryFn: () => fetchRawRegulations({ pageNumber, pageSize, searchTerm }),
    enabled: hasAccount,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return { updateMutation, regulation, regulations, isLoading, isRegulationsFetching: isFetching };
};